import React from 'react';
import { Clock, LogOut, X } from 'lucide-react';

export type LogoutPromptVariant = 'timeclock' | 'timers' | 'both';

interface LogoutWithActiveWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** What's active: timeclock only, job timers only, or both */
  variant: LogoutPromptVariant;
  /** Number of active job timers (for display when variant is 'timers' or 'both') */
  activeTimerCount?: number;
  onStopAndLogout: () => Promise<void>;
  loading?: boolean;
}

function getMessage(variant: LogoutPromptVariant, activeTimerCount: number): { title: string; body: string; button: string } {
  switch (variant) {
    case 'timeclock':
      return {
        title: "You're clocked in",
        body: 'Stop your timeclock before logging out?',
        button: 'Stop timeclock and log out',
      };
    case 'timers':
      return {
        title: 'Active job timer' + (activeTimerCount > 1 ? 's' : ''),
        body: `You have ${activeTimerCount} job timer${activeTimerCount > 1 ? 's' : ''} running. Stop ${activeTimerCount > 1 ? 'them' : 'it'} before logging out?`,
        button: 'Stop timer' + (activeTimerCount > 1 ? 's' : '') + ' and log out',
      };
    case 'both':
      return {
        title: "You're clocked in and have active job timer" + (activeTimerCount > 1 ? 's' : ''),
        body: `Stop your timeclock and ${activeTimerCount} job timer${activeTimerCount > 1 ? 's' : ''} to log out?`,
        button: 'Stop timeclock and timer' + (activeTimerCount > 1 ? 's' : '') + ', then log out',
      };
  }
}

export default function LogoutWithActiveWorkModal({
  isOpen,
  onClose,
  variant,
  activeTimerCount = 0,
  onStopAndLogout,
  loading = false,
}: LogoutWithActiveWorkModalProps) {
  if (!isOpen) return null;

  const { title, body, button } = getMessage(variant, activeTimerCount);

  return (
    <div className="fixed inset-0 z-[100] min-h-screen flex items-center justify-center bg-black/50 p-4" onClick={() => !loading && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden mx-auto my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="px-5 pb-5 text-sm text-gray-500">{body}</p>
        <div className="flex gap-2 px-5 pb-5">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onStopAndLogout()}
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Stopping…
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4" />
                {button}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
